<template>
  <v-layout row wrap class="mb-5">
    <v-flex xs12 class="text-xs-center">
      <v-container ma-0>
        <u>
          <h1 class="secondary--text">
            {{ $t("screener.monetization.pricing_plan_overview") }}
          </h1>
        </u>
      </v-container>
    </v-flex>

    <v-flex xs12 v-if="pricingPlan">
      <v-container ma-0>
        <v-alert
          v-if="isGeoBlocked"
          v-model="isGeoBlocked"
          dismissible
          type="error"
          class="mb-2"
          >{{ $t("screener.geo_blocked") }}</v-alert
        >
        <v-card>
          <v-card-text class="pa-4 background--text primary">
            <GroupTicketHeader
              :ticket="pricingPlan"
              :isGeoBlocked="isGeoBlocked"
              @initiate-enrollment="initiateEnrollment"
            ></GroupTicketHeader>
          </v-card-text>
          <v-card-text class="pa-4">
            <v-layout row wrap>
              <v-flex xs12 v-if="pricingPlan.description">
                <div class="subheading secondary--text">
                  {{ pricingPlan.description }}
                </div>
              </v-flex>
            </v-layout>
            <v-divider v-if="pricingPlan.description" class="my-3"></v-divider>

            <h3 class="secondary--text">{{ $t("screenings.title") }}</h3>

            <v-data-table
              :items="pricingPlan.screenings"
              hide-actions
              hide-headers
            >
              <template v-slot:items="props">
                <tr>
                  <td class="py-3 px-0" width="8%">
                    <v-card width="100px">
                      <v-layout row>
                        <v-flex xs12>
                          <!-- Show thumbnail of screener -->
                          <template v-if="props.item.thumbnail_url">
                            <v-img
                              slot="activator"
                              :src="props.item.thumbnail_url"
                              contain
                            ></v-img>
                          </template>
                          <!-- Show thumbnail of first film if thumbnail is not available on screener -->
                          <template
                            v-else-if="
                              props.item.films &&
                              props.item.films[0] &&
                              props.item.films[0].thumburls
                            "
                          >
                            <v-img
                              slot="activator"
                              :src="props.item.films[0].thumburls"
                              contain
                            ></v-img>
                          </template>
                          <template v-else>{{ props.item.title }}</template>
                        </v-flex>
                      </v-layout>
                    </v-card>
                  </td>
                  <td class="pa-3" width="70%">
                    <div>
                      <!-- Title -->
                      <h2 class="font-weight-regular secondary--text">
                        <router-link
                          :to="{
                            name: 'screeners.overview',
                            params: { screener_id: props.item.id },
                          }"
                          class="secondary--text"
                        >
                          {{ props.item.title }}</router-link
                        >
                      </h2>
                      <!-- Start date -->
                      <div class="subheading secondary--text text--lighten-2">
                        {{
                          moment
                            .utc(props.item.screening_starts_at)
                            .local()
                            .format("lll")
                        }}
                      </div>
                      <!-- Seats Status -->
                      <div>
                        <span class="subheading secondary--text"
                          >{{ $t("screener.seats.title") }}
                        </span>
                        <span
                          class="subheading"
                          v-html="getSeatsStatus(props.item)"
                        ></span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span
                      class="subheading"
                      v-for="(film, index) in props.item.films"
                      :key="film.id"
                      >{{ film.director_name }}
                      <span v-if="index + 1 < props.item.films.length"
                        >,</span
                      ></span
                    >
                  </td>
                  <td width="15%">
                    <!-- Alert for geo-blocking -->
                    <v-tooltip
                      top
                      v-if="props.item.is_geo_blocked"
                      color="primary"
                    >
                      <template v-slot:activator="{ on }">
                        <v-icon
                          v-if="props.item.is_geo_blocked"
                          v-on="on"
                          small
                          color="error"
                          class="px-1"
                          >mdi-alert</v-icon
                        >
                      </template>
                      <span class="background--text text--darken-3 body-1">{{
                        $t("screener.geo_blocked")
                      }}</span>
                    </v-tooltip>

                    <EndedIndicator
                      v-if="!isDateFuture(props.item.screening_ends_at)"
                    ></EndedIndicator>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
        <v-layout
          v-if="!hasGroupTicketPurchased(pricingPlan.id)"
          row
          wrap
          class="text-xs-center my-3"
        >
          <v-flex xs12>
            <v-btn :disabled="isGeoBlocked" @click.stop="initiateEnrollment" color="primary background--text">{{
              $t("screener.btn.buy_pass")
            }}</v-btn>
          </v-flex>
        </v-layout>
      </v-container>
    </v-flex>
    <Enrollment :dialog.sync="dialog" :modeOfUse="DEFINES.ENROLLMENT_COMP.MODE_OF_USE.PASS" :pass="pricingPlan"></Enrollment>
  </v-layout>
</template>

<script>
import GroupTicketHeader from "@/components/monetization/GroupTicketHeader";
import { hasGroupTicketPurchased } from "@/mixins/monetization";
import { getSeatsStatus } from "@/mixins/screening";
import Enrollment from "@/components/company/Enrollment";
import EndedIndicator from "@/components/screener/EndedIndicator";
import { isDateFuture } from "@/helpers";
import DEFINES from "@/defines";

// Data
const data = { pricingPlan: null, dialog: false };

// Components
const components = {
  GroupTicketHeader,
  Enrollment,
  EndedIndicator,
};

// Mixins
const mixins = [hasGroupTicketPurchased, getSeatsStatus];

// Methods
const computed = {
  isGeoBlocked: function(){
    return this.pricingPlan
            ? this.pricingPlan.screenings.some(screening => screening.is_geo_blocked === true)
            : false;
  }
};

const methods = {
  async getPricingPlan() {
    if (!this.$route.params.pricing_id) {
      return;
    }
    try {
      this.pricingPlan = await this.$store.dispatch("monetization/getPricing", {
        pricing_id: this.$route.params.pricing_id,
      });
    } catch (error) {
      this.$store.dispatch("app/showMessage", {
        message: error.message,
        color: this.DEFINES.ERROR_COLOR,
      });
    }
  },
  initiateEnrollment() {
    this.dialog = true;
  },
  isDateFuture(date) {
    return isDateFuture(date);
  },
};

export default {
  name: "GroupTicketOverview",
  data() {
    return data;
  },
  components: components,
  mixins: mixins,
  computed: computed,
  mounted() {
    this.getPricingPlan();
  },
  methods: methods,
};
</script>

<style></style>
